import { Fragment, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, Badge, EmptyState } from "@/components/common/States";

const ENTITIES = ["company", "branch", "user", "role", "system"];
const LIMIT = 25;

const Changes = ({ changes }) => {
  const entries = Object.entries(changes || {});
  if (!entries.length) return <span className="text-xs text-muted-foreground">No field changes recorded.</span>;
  return (
    <div className="grid gap-1.5 text-xs font-mono-g">
      {entries.map(([k, v]) => (
        <div key={k} className="flex flex-wrap gap-2"><span className="text-gold">{k}</span><span className="text-red-600/80 line-through">{JSON.stringify(v?.from)}</span><span>→</span><span className="text-emerald-700">{JSON.stringify(v?.to)}</span></div>
      ))}
    </div>
  );
};

export default function AuditPage() {
  const { can } = useSession();
  const [q, setQ] = useState("");
  const [entity, setEntity] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const params = { q: q || undefined, entity_type: entity === "all" ? undefined : entity, date_from: from || undefined, date_to: to || undefined, page, limit: LIMIT };
  const { data, loading, error, reload } = useFetch("/audit", params, can("audit.view"));
  if (!can("audit.view")) return <NoAccess perm="audit.view" />;
  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const reset = (fn) => (v) => { fn(v?.target ? v.target.value : v); setPage(1); };

  return (
    <div data-testid="audit-page">
      <PageHeader eyebrow="Administration" title="Audit Logs" subtitle="Immutable trail of security and administrative events for the selected scope." />
      <div className="g39-card overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_160px_160px] gap-3 p-4 border-b bg-secondary/40">
          <div className="relative"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input className="pl-9 bg-white" placeholder="Search summary, user, action" value={q} onChange={reset(setQ)} data-testid="audit-search-input" /></div>
          <Select value={entity} onValueChange={reset(setEntity)}>
            <SelectTrigger className="bg-white" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All entities</SelectItem>{ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" className="bg-white" value={from} onChange={reset(setFrom)} data-testid="audit-date-from" />
          <Input type="date" className="bg-white" value={to} onChange={reset(setTo)} data-testid="audit-date-to" />
        </div>
        {loading && <LoadingRows />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.items.length && <EmptyState title="No events match" />}
        {data?.items.length > 0 && (
          <div className="overflow-x-auto">
            <Table data-testid="audit-table">
              <TableHeader><TableRow><TableHead className="w-8" /><TableHead>Time</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Summary</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((e) => (
                  <Fragment key={e.id}>
                    <TableRow className="cursor-pointer" onClick={() => setOpen(open === e.id ? null : e.id)} data-testid={`audit-row-${e.id}`}>
                      <TableCell><ChevronDown className={`h-4 w-4 transition-transform ${open === e.id ? "rotate-180" : ""}`} /></TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(e.at)}</TableCell>
                      <TableCell className="text-sm"><div>{e.actor_name || e.actor_email}</div><div className="text-[11px] text-muted-foreground">{e.actor_email}</div></TableCell>
                      <TableCell><Badge tone="gold" className="font-mono-g">{e.action}</Badge></TableCell>
                      <TableCell className="text-sm max-w-md truncate">{e.summary}</TableCell>
                    </TableRow>
                    {open === e.id && (
                      <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                        <TableCell />
                        <TableCell colSpan={4} className="py-4" data-testid={`audit-detail-${e.id}`}>
                          <Changes changes={e.changes} />
                          <div className="text-[11px] text-muted-foreground mt-3">Entity: {e.entity_type} {e.entity_id} · Role: {e.actor_role || "—"} · IP: {e.ip || "—"}</div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && data.total > LIMIT && (
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm" data-testid="audit-pagination">
            <span className="text-muted-foreground">{data.total} events · page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" disabled={page === pages} onClick={() => setPage(page + 1)} data-testid="audit-next-page"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
